import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Loader2, User, Calendar, Mail, Phone, MapPin,
  CheckCircle2, Circle, ClipboardList, Package, ChevronDown, ChevronUp,
  Check,
} from 'lucide-react'
import { useSolicitudDetalle } from '../hooks/useSolicitudes'
import { useUsuariosTonal } from '../hooks/useCampanias'
import type { ActualizarSeguimientoPayload } from '../types'

interface SeguimientoModalProps {
  solicitudId: number | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSave: (payload: ActualizarSeguimientoPayload) => void
  isSaving: boolean
  campaniaTitulo?: string
  campaniaImagen?: string | null
}

export function SeguimientoModal({
  solicitudId,
  open,
  onOpenChange,
  onSave,
  isSaving,
  campaniaTitulo,
  campaniaImagen,
}: SeguimientoModalProps) {
  const { data: solicitud, isLoading } = useSolicitudDetalle(solicitudId || 0)
  const { data: usuarios } = useUsuariosTonal()
  const [showDetalles, setShowDetalles] = useState(false)

  const [form, setForm] = useState({
    estado_contacto: '',
    estado_envio: '',
    estado_entrega: '',
    fecha_envio: '',
    fecha_envio_confirmada: false,
    direccion_envio: '',
    numero_guia: '',
    persona_recibio: '',
    fecha_entrega: '',
    notas: '',
    responsable_id: '' as string,
  })

  useEffect(() => {
    if (solicitud) {
      setForm({
        estado_contacto: solicitud.estado_contacto,
        estado_envio: solicitud.estado_envio,
        estado_entrega: solicitud.estado_entrega,
        fecha_envio: solicitud.fecha_envio_valor || '',
        fecha_envio_confirmada: solicitud.fecha_envio_confirmada,
        direccion_envio: solicitud.direccion_envio || '',
        numero_guia: solicitud.numero_guia || '',
        persona_recibio: solicitud.persona_recibio || '',
        fecha_entrega: solicitud.fecha_entrega?.slice(0, 10) || '',
        notas: solicitud.notas || '',
        responsable_id: solicitud.responsable_id?.toString() || '',
      })
    }
  }, [solicitud])

  const handleSave = () => {
    if (!solicitudId) return
    const payload: ActualizarSeguimientoPayload = {
      solicitud_id: solicitudId,
      estado_contacto: form.estado_contacto,
      estado_envio: form.estado_envio,
      estado_entrega: form.estado_entrega,
      fecha_envio: form.fecha_envio || undefined,
      fecha_envio_confirmada: form.fecha_envio_confirmada,
      direccion_envio: form.direccion_envio || undefined,
      numero_guia: form.numero_guia || undefined,
      persona_recibio: form.persona_recibio || undefined,
      fecha_entrega: form.fecha_entrega || undefined,
      notas: form.notas || undefined,
      responsable_id: form.responsable_id ? Number(form.responsable_id) : null,
    }
    onSave(payload)
  }

  // Flujo de trabajo: tareas derivadas del estado
  const tareas = solicitud ? [
    { label: 'Contactar al médico', done: form.estado_contacto === 'Contactado' },
    { label: 'Recolectar información de envío', done: !!form.direccion_envio },
    { label: 'Confirmar fecha de envío', done: form.fecha_envio_confirmada },
    { label: 'Enviar muestra', done: form.estado_envio === 'Enviado' },
    { label: 'Guardar número de guía', done: !!form.numero_guia },
    { label: 'Actualizar cuando reciba', done: form.estado_entrega === 'Entregado' },
    { label: 'Agregar información de receptor', done: !!form.persona_recibio },
  ] : []

  const tareasCompletadas = tareas.filter(t => t.done).length
  const totalTareas = tareas.length
  const porcentaje = solicitud?.porcentaje_progreso ?? (totalTareas > 0 ? Math.round((tareasCompletadas / totalTareas) * 100) : 0)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto p-0 gap-0">
        {isLoading ? (
          <div className="space-y-4 p-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : solicitud ? (
          <>
            {/* Header */}
            <div className="flex items-start justify-between p-6 pb-4">
              <div className="flex items-center gap-4">
                {campaniaImagen ? (
                  <img src={campaniaImagen} alt="" className="h-12 w-12 rounded-lg object-contain" />
                ) : (
                  <div className="h-12 w-12 rounded-lg bg-teal-50 dark:bg-teal-900/30 flex items-center justify-center">
                    <Package className="h-6 w-6 text-teal-500" />
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-bold">Gestión de Seguimiento</h2>
                    {campaniaTitulo && (
                      <>
                        <span className="text-muted-foreground">·</span>
                        <span className="text-sm text-muted-foreground">{campaniaTitulo}</span>
                      </>
                    )}
                  </div>
                  <div className="mt-1">
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span>Progreso del flujo</span>
                      <span>{tareasCompletadas} de {totalTareas} tareas</span>
                    </div>
                    <div className="w-64 h-2 rounded-full bg-teal-500/10 mt-1">
                      <div
                        className="h-2 rounded-full transition-all"
                        style={{ background: 'linear-gradient(90deg, #14b8a6, #0f766e)', width: `${(tareasCompletadas / totalTareas) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <span className="text-3xl font-bold text-teal-600 dark:text-teal-400">{porcentaje}%</span>
            </div>

            {/* Datos del solicitante */}
            <div className="mx-6 p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
                <div>
                  <dt className="text-xs text-muted-foreground mb-0.5">Nombre Completo</dt>
                  <dd className="flex items-center gap-1.5">
                    <User className="h-3.5 w-3.5 text-muted-foreground" />
                    {solicitud.nombre_completo}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground mb-0.5">Fecha de Solicitud</dt>
                  <dd className="flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                    {solicitud.fecha_solicitud}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground mb-0.5">Correo Electrónico</dt>
                  <dd className="flex items-center gap-1.5">
                    <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                    {solicitud.correo || '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground mb-0.5">Teléfono / WhatsApp</dt>
                  <dd className="flex items-center gap-1.5">
                    <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                    {solicitud.telefono || '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground mb-0.5">Ubicación</dt>
                  <dd className="flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                    {[solicitud.ciudad, solicitud.provincia].filter(Boolean).join(', ') || '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground mb-0.5">Aceptaciones</dt>
                  <dd className="flex items-center gap-3">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium ${solicitud.aceptar_terminos ? 'text-teal-600 dark:text-teal-400' : 'text-muted-foreground'}`}>
                      <Check className="h-3 w-3" /> T&C
                    </span>
                    <span className={`inline-flex items-center gap-1 text-xs font-medium ${solicitud.autorizar_envio_mm ? 'text-teal-600 dark:text-teal-400' : 'text-muted-foreground'}`}>
                      <Check className="h-3 w-3" /> Envío MM
                    </span>
                  </dd>
                </div>
              </div>
            </div>

            {/* Flujo de Trabajo + Imagen producto */}
            <div className="px-6 py-4">
              <div className="flex gap-6">
                {/* Checklist */}
                <div className="flex-1">
                  <h3 className="font-semibold flex items-center gap-1.5 mb-3">
                    <ClipboardList className="h-4 w-4 text-teal-500" />
                    Flujo de Trabajo
                  </h3>
                  <ul className="space-y-2">
                    {tareas.map((t, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        {t.done ? (
                          <CheckCircle2 className="h-4 w-4 text-teal-500 shrink-0" />
                        ) : (
                          <Circle className="h-4 w-4 text-gray-300 dark:text-gray-600 shrink-0" />
                        )}
                        <span className={t.done ? 'text-teal-700 dark:text-teal-400 font-medium' : 'text-muted-foreground'}>
                          {t.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                {/* Imagen producto */}
                <div className="w-48 shrink-0">
                  <div className="text-xs text-muted-foreground text-right mb-1">
                    × 1 muestra
                  </div>
                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 h-40 flex items-center justify-center">
                    {campaniaImagen ? (
                      <img src={campaniaImagen} alt="Producto" className="max-h-36 object-contain rounded" />
                    ) : (
                      <Package className="h-12 w-12 text-gray-300 dark:text-gray-600" />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Detalles de seguimiento (collapsible) */}
            <div className="mx-6 mb-2">
              <button
                onClick={() => setShowDetalles(!showDetalles)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <span className="flex items-center gap-2 text-sm font-medium">
                  <ClipboardList className="h-4 w-4 text-teal-500" />
                  Detalles de Seguimiento
                </span>
                {showDetalles ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
              </button>

              {showDetalles && (
                <div className="mt-3 space-y-4 px-1">
                  {/* Estados */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Contacto</Label>
                      <select
                        value={form.estado_contacto}
                        onChange={(e) => setForm((f) => ({ ...f, estado_contacto: e.target.value }))}
                        className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm focus:outline-none focus:border-zinc-400"
                      >
                        <option value="Pendiente">Pendiente</option>
                        <option value="Contactado">Contactado</option>
                        <option value="Repetido">Repetido</option>
                      </select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Envío</Label>
                      <select
                        value={form.estado_envio}
                        onChange={(e) => setForm((f) => ({ ...f, estado_envio: e.target.value }))}
                        className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm focus:outline-none focus:border-zinc-400"
                      >
                        <option value="Pendiente">Pendiente</option>
                        <option value="Envio programado">Programado</option>
                        <option value="Enviado">Enviado</option>
                        <option value="Repetido">Repetido</option>
                      </select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Entrega</Label>
                      <select
                        value={form.estado_entrega}
                        onChange={(e) => setForm((f) => ({ ...f, estado_entrega: e.target.value }))}
                        className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm focus:outline-none focus:border-zinc-400"
                      >
                        <option value="Pendiente">Pendiente</option>
                        <option value="Entregado">Entregado</option>
                        <option value="Repetido">Repetido</option>
                      </select>
                    </div>
                  </div>

                  <Separator />

                  {/* Info envío */}
                  <div className="grid grid-cols-2 gap-3">
                    {solicitud.opciones_fecha_envio && solicitud.opciones_fecha_envio.length > 0 ? (
                      <div className="space-y-1">
                        <Label className="text-xs">Fecha de envío</Label>
                        <select
                          value={form.fecha_envio}
                          onChange={(e) => setForm((f) => ({ ...f, fecha_envio: e.target.value }))}
                          className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm focus:outline-none focus:border-zinc-400"
                        >
                          <option value="">Seleccionar fecha</option>
                          {solicitud.opciones_fecha_envio.map((op) => (
                            <option key={op} value={op}>{op}</option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <Label className="text-xs">Fecha de envío</Label>
                        <Input className="h-9" value={form.fecha_envio} onChange={(e) => setForm((f) => ({ ...f, fecha_envio: e.target.value }))} placeholder="Fecha de envío" />
                      </div>
                    )}
                    <div className="space-y-1">
                      <Label className="text-xs">Fecha de entrega</Label>
                      <Input className="h-9" type="date" value={form.fecha_entrega} onChange={(e) => setForm((f) => ({ ...f, fecha_entrega: e.target.value }))} />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">Dirección de envío</Label>
                    <Input className="h-9" value={form.direccion_envio} onChange={(e) => setForm((f) => ({ ...f, direccion_envio: e.target.value }))} />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Número de guía</Label>
                      <Input className="h-9" value={form.numero_guia} onChange={(e) => setForm((f) => ({ ...f, numero_guia: e.target.value }))} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Persona que recibió</Label>
                      <Input className="h-9" value={form.persona_recibio} onChange={(e) => setForm((f) => ({ ...f, persona_recibio: e.target.value }))} />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">Responsable</Label>
                    <select
                      value={form.responsable_id}
                      onChange={(e) => setForm((f) => ({ ...f, responsable_id: e.target.value }))}
                      className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm focus:outline-none focus:border-zinc-400"
                    >
                      <option value="">Sin asignar</option>
                      {usuarios?.map((u) => (
                        <option key={u.id} value={u.id.toString()}>{u.nombre}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">Notas</Label>
                    <Textarea value={form.notas} onChange={(e) => setForm((f) => ({ ...f, notas: e.target.value }))} rows={2} />
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
              {showDetalles && (
                <Button onClick={handleSave} disabled={isSaving}>
                  {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Guardar
                </Button>
              )}
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
